import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { EyeOff, ArrowLeft, CheckCircle2, MessageSquare, Save, Loader2 } from "lucide-react";

// Blinded clinician review workspace. The clinician sees the patient's raw
// item answers only — no auto-score, no band, no label — and types their
// impression. Both the comment and the (hidden-at-input-time) auto-score
// land side-by-side in the per-instrument XLSX bundle for later correlation.
export default function ClinicianBlindedReview() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {response, instrument}
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const loadQueue = () => {
    setLoadingQueue(true);
    api.get("/clinician/blinded-responses").then((r) => {
      setQueue(r.data.responses || []);
      setReviewedCount(r.data.reviewed_count || 0);
    }).catch(() => toast.error("Could not load review queue"))
      .finally(() => setLoadingQueue(false));
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!["PSYCHOLOGIST", "PSYCHIATRIST"].includes(user.role)) {
      toast.error("Clinician access only");
      nav("/");
      return;
    }
    loadQueue();
  }, [user, loading, nav]);

  const openResponse = async (rid) => {
    setSelectedId(rid);
    setDetail(null);
    setComment("");
    try {
      const r = await api.get(`/clinician/blinded-responses/${rid}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load response");
      setSelectedId(null);
    }
  };

  const save = async () => {
    if (!selectedId) return;
    if (comment.trim().length < 3) {
      toast.error("Please write a short impression before saving");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clinician/comments", {
        response_id: selectedId, comment: comment.trim(),
      });
      toast.success("Blinded comment saved");
      // Return to queue and refresh so this response drops off.
      setSelectedId(null); setDetail(null); setComment("");
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container py-10">
        <Button variant="ghost" size="sm" onClick={() => nav("/clinician")}
                className="mb-4 -ml-2 text-muted-foreground"
                data-testid="blinded-back-btn">
          <ArrowLeft size={14} /> Back to cases
        </Button>
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs">
              <EyeOff size={12} /> Blinded review
            </div>
            <h1 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight mt-3"
                data-testid="blinded-title">
              Comment without seeing the score
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              You'll see the patient's raw answers only — no auto-score, no band. Type your clinical impression and both will
              be joined in the per-instrument export for correlation later.
            </p>
          </div>
          <Badge variant="outline" data-testid="blinded-reviewed-count">
            {reviewedCount} reviewed by you
          </Badge>
        </div>

        {!selectedId && (
          <Card className="mano-card" data-testid="blinded-queue">
            <CardContent className="p-6">
              {loadingQueue && (
                <div className="text-sm text-muted-foreground py-3 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading queue…
                </div>
              )}
              {!loadingQueue && queue.length === 0 && (
                <div className="text-sm text-muted-foreground py-6 text-center"
                     data-testid="blinded-queue-empty">
                  Nothing pending — you've reviewed every open response.
                </div>
              )}
              {!loadingQueue && queue.length > 0 && (
                <div className="space-y-2" data-testid="blinded-queue-list">
                  {queue.map((r) => (
                    <button key={r.response_id} type="button"
                            onClick={() => openResponse(r.response_id)}
                            data-testid={`blinded-queue-item-${r.response_id}`}
                            className="w-full text-left rounded-lg border border-border/60 hover:bg-muted/40 p-3 text-sm transition-colors mano-lift">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{r.instrument_id}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                            {(r.response_id || "").slice(0, 8)}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(r.created_at || "").slice(0, 10)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {selectedId && (
          <Card className="mano-card" data-testid="blinded-detail">
            <CardContent className="p-6 md:p-8 space-y-6">
              {!detail && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading response…
                </div>
              )}
              {detail && (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Badge variant="outline" className="text-[10px]">{detail.instrument?.section}</Badge>
                      <div className="font-display text-xl mt-2" data-testid="blinded-instrument-title">
                        {detail.instrument?.title}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-1">
                        response: {String(detail.response?.response_id || "").slice(0, 8)}
                      </div>
                    </div>
                    <Badge className="bg-primary/10 text-primary border border-primary/30 text-[11px]">
                      <EyeOff size={11} /> Score hidden
                    </Badge>
                  </div>

                  <div className="space-y-2" data-testid="blinded-items">
                    {(detail.instrument?.items || []).map((it, idx) => {
                      const ans = (detail.response?.responses || {})[it.item_id];
                      return (
                        <div key={it.item_id}
                             className="rounded-lg border border-border/60 p-3 text-sm"
                             data-testid={`blinded-item-${it.item_id}`}>
                          <div className="flex items-start gap-3">
                            <span className="text-[10px] font-mono text-muted-foreground pt-0.5">{idx + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <div>{it.text || it.item_id}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                Answer: <span className="font-mono text-foreground">{ans !== undefined ? ans : "—"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="pt-2 border-t border-border/40 space-y-2">
                    <Label htmlFor="blinded-comment" className="flex items-center gap-1 text-sm">
                      <MessageSquare size={13} /> Your impression (auto-score stays hidden)
                    </Label>
                    <Textarea id="blinded-comment" value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              rows={5}
                              placeholder="What stands out? Note wording, patterns across items, risk signals…"
                              data-testid="blinded-comment-input" />
                    <div className="flex gap-2 pt-2">
                      <Button onClick={save} disabled={saving}
                              className="rounded-full mano-lift"
                              data-testid="blinded-save-btn">
                        <Save size={13} /> {saving ? "Saving…" : "Save comment"}
                      </Button>
                      <Button variant="ghost" onClick={() => { setSelectedId(null); setDetail(null); setComment(""); }}
                              className="rounded-full"
                              data-testid="blinded-cancel-btn">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
